import { z } from 'zod'

export const timeToMinutes = (time: string): number => {
  if (!time || !time.includes(':')) return 0
  const [h, m] = time.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

const timeRegex = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/

export const attendanceSettingsSchema = z
  .object({
    gracePeriodMinutes: z.coerce
      .number()
      .min(0, 'Grace period must be greater than or equal to 0'),
    lateThresholdMinutes: z.coerce
      .number()
      .min(0, 'Late threshold must be greater than or equal to 0'),
    halfDayHours: z.coerce
      .number()
      .min(0, 'Half-day hours must be greater than or equal to 0'),
    dailyWorkingHours: z.coerce
      .number()
      .positive('Daily working hours must be greater than 0'),
    absentCutoff: z.string().regex(timeRegex, 'Invalid time format (HH:mm)'),
    workStart: z.string().regex(timeRegex, 'Invalid time format (HH:mm)'),
    workEnd: z.string().regex(timeRegex, 'Invalid time format (HH:mm)'),
    biometricEnabled: z.boolean(),
    manualExcelEnabled: z.boolean(),
    shiftMode: z.string().nullable().optional(),
    shiftModeId: z.string().nullable().optional(),
    selfServiceEnabled: z.boolean().optional(),
    managerApprovalRequired: z.boolean().optional(),
    adminOverrideEnabled: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const startMin = timeToMinutes(data.workStart)
    const endMin = timeToMinutes(data.workEnd)

    if (endMin <= startMin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Work end time must be after start time',
        path: ['workEnd'],
      })
    }

    if (data.halfDayHours > data.dailyWorkingHours) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Half-day hours must be less than or equal to daily working hours',
        path: ['halfDayHours'],
      })
    }
  })

export type AttendanceSettingsSchemaType = z.infer<typeof attendanceSettingsSchema>
